import * as React from "react"
import * as ReactDOM from "react-dom/client"
import { createBrowserRouter, RouterProvider } from "react-router-dom"
import type { RouteObject } from "react-router-dom"

import * as serviceWorker from "./serviceWorker"

import * as App from "./App"
import * as FeedingReportsApi from "./api/FeedingReportsApi"
import * as FeedingStatsApi from "./api/FeedingStatsApi"
import HomepageRedirect from "./routes/HomepageRedirect"
import Login from "./routes/Login"
// import ModemsPage from './routes/ModemsPage'
// import ModemDetails from './components/modem-details/ModemDetails'
import VehiclePage from "./routes/VehiclePage"
import * as EuDataActPage from "./routes/eu-data-act/EuDataActPage"
import DataSharing from "./routes/eu-data-act/DataSharing"
import RawDataDownload from "./routes/eu-data-act/RawDataDownload"
import Transfer from "./routes/eu-data-act/Transfer"
import * as VehiclesListPage from "./routes/vehicles/VehiclesListPage"
import * as VehicleOverview from "./components/vehicle-overview/VehicleOverview"
import VehicleCanTraces from "./components/vehicle-debug/VehicleCanTraces"
import VehicleDebug from "./components/vehicle-debug/VehicleDebug"
import VehicleReportPage from "./routes/VehicleReportPage"
import TimeseriesPage from "./routes/TimeseriesPage"

import { AdminPanel } from "./routes/admin"
import { AdminCognitoUsers } from "./routes/admin/AdminCognitoUsers"
import { AdminMachines } from "./routes/admin/AdminMachines"
import { AdminOrgs } from "./routes/admin/AdminOrgs"
import { AdminUsers } from "./routes/admin/AdminUsers"
import { AdminMachineCreateEditForm } from "./routes/admin/create-edit-machine"
import { AdminOrgCreateEditForm } from "./routes/admin/create-edit-org"
import { AdminUserCreateEditForm } from "./routes/admin/create-edit-user"
import Orgs from "./routes/orgs/Orgs"
import Recipes from "./routes/orgs/recipes/Recipes"
import * as Recipe from "./routes/orgs/recipes/Recipe"
import RecipeCreateEdit from "./routes/orgs/recipes/RecipeCreateEdit"
import FeedComponents from "./routes/orgs/feed-components/FeedComponents"
import * as FeedComponent from "./routes/orgs/feed-components/FeedComponent"
import FeedComponentCreateEdit from "./routes/orgs/feed-components/FeedComponentCreateEdit"
import AnimalGroups from "./routes/orgs/animal-groups/AnimalGroups"
import * as AnimalGroup from "./routes/orgs/animal-groups/AnimalGroup"
import AnimalGroupCreateEdit from "./routes/orgs/animal-groups/AnimalGroupCreateEdit"
import LinkMachine from "./routes/orgs/LinkMachine"
import OrgDashboard from "./routes/orgs/OrgDashboard"
import OrgRouteGuard from "./routes/orgs/OrgRouteGuard"
import OrgSettings from "./routes/orgs/OrgSettings"
import Bin from "./routes/orgs/bin/Bin"
import Feedings from "./routes/orgs/stats/Feedings"
import FeedingDetails from "./routes/orgs/stats/FeedingDetails"
import GenauigkeitOverall from "./routes/orgs/stats/GenauigkeitOverall"
import VerbrauchOverall from "./routes/orgs/stats/VerbrauchOverall"
import StatsSettings from "./routes/orgs/stats/StatsSettings"
import { ErrorBoundary } from "./components"

const orgsRoutes: RouteObject = {
  path: "orgs",
  errorElement: (
    <>
      <div className="rm-list-page">
        <ErrorBoundary showTitle={false} />
      </div>
    </>
  ),
  children: [
    {
      index: true,
      element: <Orgs />,
    },
    {
      path: ":orgId",
      element: <OrgRouteGuard />,
      children: [
        {
          index: true,
          element: <OrgDashboard />,
        },
        {
          path: "edit",
          element: <OrgSettings />,
        },
        {
          path: "recipes",
          children: [
            {
              index: true,
              element: <Recipes />,
            },
            {
              path: "new",
              element: <RecipeCreateEdit />,
            },
            {
              path: ":recipeId",
              errorElement: <Recipe.ErrorComponent />,
              children: [
                {
                  index: true,
                  element: <Recipe.Component />,
                },
                {
                  path: "edit",
                  element: <RecipeCreateEdit />,
                },
              ],
            },
          ],
        },
        {
          path: "feed-components",
          children: [
            {
              index: true,
              element: <FeedComponents />,
            },
            {
              path: "new",
              element: <FeedComponentCreateEdit />,
            },
            {
              path: ":feedComponentId",
              errorElement: <FeedComponent.ErrorComponent />,
              children: [
                {
                  index: true,
                  element: <FeedComponent.Component />,
                  loader: FeedingStatsApi.loaderCustomRange,
                },
                { path: "edit", element: <FeedComponentCreateEdit /> },
              ],
            },
          ],
        },
        {
          path: "animal-groups",
          children: [
            {
              index: true,
              element: <AnimalGroups />,
            },
            {
              path: "new",
              element: <AnimalGroupCreateEdit />,
            },
            {
              path: ":animalGroupId",
              errorElement: <AnimalGroup.ErrorComponent />,
              children: [
                {
                  index: true,
                  element: <AnimalGroup.Component />,
                  loader: FeedingStatsApi.loaderCustomRange,
                },
                {
                  path: "edit",
                  element: <AnimalGroupCreateEdit />,
                },
              ],
            },
          ],
        },
        {
          path: "link-machine",
          element: <LinkMachine />,
        },
        {
          path: "bin",
          element: <Bin />,
        },
        {
          path: "stats",
          children: [
            {
              path: "feedings",
              children: [
                {
                  index: true,
                  element: <Feedings />,
                },
                {
                  path: ":feedingReportId",
                  element: <FeedingDetails />,
                  loader: FeedingReportsApi.loader,
                },
              ],
            },
            {
              path: "accuracy",
              element: <GenauigkeitOverall />,
            },
            {
              path: "v",
              element: <VerbrauchOverall />,
            },
            {
              path: "settings",
              element: <StatsSettings />,
            },
          ],
        },
      ],
    },
  ],
}
const vehiclesRoutes: RouteObject = {
  path: "vehicles",
  id: "vehicles-page",
  loader: VehiclesListPage.loader,
  errorElement: (
    <>
      <div className="rm-list-page">
        <ErrorBoundary showTitle={false} />
      </div>
    </>
  ),
  children: [
    {
      index: true,
      element: <VehiclesListPage.Component />,
      // TODO: Create a generic Error Component, or Error Page
      // if (error) {
      //   console.error('Fehler in VehiclesList')
      //   return <div>Fehler beim Laden der Daten: {error}</div>
      // }
      // if (!data || !Array.isArray(data) || data.length < 1) {
      //   return <div>Fehler beim Laden der Daten: Keine Daten gefunden</div>
      // }
    },
    {
      path: ":vin",
      element: <VehiclePage />,
      children: [
        {
          index: true,
          loader: VehicleOverview.loader,
          element: <VehicleOverview.Component />,
        },
        {
          path: "can-traces",
          element: <VehicleCanTraces />,
        },
        {
          path: "debug",
          element: <VehicleDebug />,
        },
        {
          path: "report",
          element: <VehicleReportPage />,
        },
        {
          path: "eu-data-act",
          children: [
            {
              index: true,
              element: <EuDataActPage.Component />,
              loader: VehiclesListPage.loader,
            },
            {
              path: "raw-data",
              element: <RawDataDownload />,
            },
            // @todo Is this a valid child route?
            // {
            //   path: "raw-data/:date",
            //   element: <RawDataDownload />,
            // },
            {
              path: "data-sharing",
              element: <DataSharing />,
            },
            {
              path: "transfer",
              element: <Transfer />,
            },
          ],
        },
        {
          path: "history/:pgn/:sourceAddress/:spn/",
          element: <TimeseriesPage mode="pgn" />,
        },
        {
          path: "history/dtcs/",
          element: <TimeseriesPage mode="dtcs" />,
        },
      ],
    },
  ],
}
const adminRoutes: RouteObject = {
  path: "admin",
  errorElement: (
    <>
      <div className="rm-list-page">
        <ErrorBoundary showTitle={false} />
      </div>
    </>
  ),
  children: [
    {
      index: true,
      element: <AdminPanel />,
    },
    {
      path: "new-users",
      element: <AdminCognitoUsers />,
    },
    {
      path: "users",
      children: [
        {
          index: true,
          element: <AdminUsers />,
        },
        {
          path: ":userId",
          element: <AdminUserCreateEditForm />,
        },
      ],
    },
    {
      path: "orgs",
      children: [
        {
          index: true,
          element: <AdminOrgs />,
        },
        {
          path: ":orgId",
          element: <AdminOrgCreateEditForm />,
        },
      ],
    },
    {
      path: "machines",
      children: [
        {
          index: true,
          element: <AdminMachines />,
        },
        {
          path: ":machineId",
          element: <AdminMachineCreateEditForm />,
        },
      ],
    },
  ],
}

const router = createBrowserRouter([
  {
    path: "/",
    element: <App.Component />,
    errorElement: <ErrorBoundary />,
    id: "root",
    loader: App.loader,
    children: [
      {
        index: true,
        element: <HomepageRedirect />,
        errorElement: (
          <>
            <div className="rm-list-page">
              <ErrorBoundary showTitle={false} />
            </div>
          </>
        ),
      },
      {
        path: "/login",
        element: <Login />,
      },
      orgsRoutes,
      vehiclesRoutes,
      adminRoutes,
      {
        path: "*",
        element: (
          <main style={{ padding: "1rem" }}>
            <p>Hier gibt's nichts zu sehen!</p>
          </main>
        ),
      },
      // Alternative:
      // {
      //   path: "*",
      //   element: <Navigate to="/" replace />
      // }
    ],
  },
])

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <RouterProvider router={router} />
  </React.StrictMode>
)

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: http://bit.ly/CRA-PWA
serviceWorker.unregister()
